import apiConfig from "../ApiConfig";

export const getEmployeeUser = async () => {
  const response = await apiConfig.get(
    `/api/team-collaboration/api/employee-master/`,
  );
  if (response.data) {
    return response.data;
  }
  throw new Error(response.data.message);
};

export const disableUserApi = (empId, payload, userId) => {
  return apiConfig
    .patch(
      `/api/team-collaboration/employee-status/${empId}/disable/`,
      payload,
    )
    .then((res) => res.data);
};

export const enableUserApi = (empId, payload, userId) => {
  return apiConfig
    .patch(
      `/api/team-collaboration/employee-status/${empId}/enable/`,
      payload,
    )
    .then((res) => res.data);
};

export const terminateUserApi = (empId, payload, userId) => {
  return apiConfig
    .patch(
      `/api/team-collaboration/employee-status/${empId}/terminate/`,
      payload,
    )
    .then((res) => res.data);
};

export const getEndUsers = async (userId, page = 1, pageSize = 20) => {
  try {
    const params = new URLSearchParams({
      user_id: userId,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    const res = await apiConfig.get(
      `/api/team-collaboration/members/?${params.toString()}`,
    );
    return res.data;
  } catch (error) {
    console.error("❌ getEndUsers API error:", error);
    throw error;
  }
};

export const getCompanyByUser = async (userId) => {
  try {
    const res = await apiConfig.get(
      `/api/customer-master/company/${userId}/`,
    );
    return res.data;
  } catch (error) {
    if (error.response?.status === 404) return null;
    console.error("❌ getCompanyByUser API error:", error);
    throw error;
  }
};

export const getRelievedEmployees = async ({
  userId,
  page = 1,
  pageSize = 20,
  search = "",
  token,
}) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      user_id: userId,
    });

    if (search && search.trim()) {
      params.append("search", search.trim());
    }

    const res = await apiConfig.get(
      `/api/team-collaboration/terminated-employees/?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  } catch (err) {
    console.error("❌ getRelievedEmployees API error:", err);
    throw err;
  }
};

export const getAllUsers = async () => {
  try {
    const res = await apiConfig.get(
      `/api/team-collaboration/api/auth/users/`,
    );
    return res.data;
  } catch (error) {
    console.error("❌ getAllUsers API error:", error);
    throw error;
  }
};

export const getCompanyUsers = async (
  customerId,
  page = 1,
  pageSize = 20,
  search = "",
) => {
  try {
    const params = new URLSearchParams({
      customer_master_id: customerId,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (search && search.trim().length > 0) {
      params.append("search", search.trim());
    }
    const res = await apiConfig.get(
      `/api/team-collaboration/company-users/?${params.toString()}`,
    );
    return res.data; // expects { results: [...] }
  } catch (err) {
    console.error("Error in getCompanyUsers:", err);
    return { results: [] };
  }
};

export const getReportingToEmployees = async (roleId, customerMasterId) => {
  try {
    const params = new URLSearchParams({
      role_id: roleId,
      customer_master_id: customerMasterId,
    });

    const res = await apiConfig.get(
      `/api/team-collaboration/roles-reporting-to/?${params.toString()}`,
    );

    if (!res.data) {
      throw new Error("Invalid response from reporting-to API");
    }

    return res.data;
  } catch (err) {
    console.error("Error in getReportingToEmployees:", err);
    return { status: "error", error: err.message || err };
  }
};

export const getTeamCompanyUsers = async (
  customerId,
  page = 1,
  pageSize = 20,
  search = "",
) => {
  try {
    const params = new URLSearchParams({
      customer_master_id: customerId,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (search && search.trim().length > 0) {
      params.append("search", search.trim());
    }

    // debug: log final URL (remove later)
    console.log(
      "[getTeamCompanyUsers] URL:",
      `/api/team-collaboration/team-company-users/?${params.toString()}`,
    );

    const res = await apiConfig.get(
      `/api/team-collaboration/team-company-users/?${params.toString()}`,
    );
    return res.data; // expects { results: [...] }
  } catch (err) {
    console.error("Error in getTeamCompanyUsers:", err);
    return { results: [] };
  }
};

// TeamCollabApis.js (add)
export const getBgvList = async ({ token, ...params } = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    if (params[k] !== undefined && params[k] !== null)
      queryParams.append(k, params[k]);
  });

  const config = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};

  const res = await apiConfig.get(
    `/api/team-collaboration/bgv/?${queryParams.toString()}`,
    config,
  );
  return res.data; // returns { results: [...] }
};

export const getPerformanceUsers = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null) {
      queryParams.append(key, params[key]);
    }
  });

  const res = await apiConfig.get(
    `/api/team-collaboration/users/?${queryParams.toString()}`,
  );
  return res.data;
};

// ===============================
// TeamCollabApis.js (ADD ONLY)
// ===============================

// PERFORMANCE
export const getPerformanceByUser = async (userId, token) => {
  const res = await apiConfig.get(
    `/api/team-collaboration/performance/?user_id=${userId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

// RELIEVED (legacy endpoint used in loadData)
export const getRelievedEmployeesLegacy = async (customerMasterId, token) => {
  const res = await apiConfig.get(
    `/api/team-collaboration/terminated-employees/user_id=${customerMasterId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

// CUSTOMER ADMINS (training + policy use same API)
export const getCustomerAdmins = async (userId, token) => {
  const res = await apiConfig.get(
    `/api/team-collaboration/customer-admins/?user_id=${userId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

// MEMBERS (non customer_admin)
export const getMembersByUser = async (userId) => {
  const res = await apiConfig.get(
    `/api/team-collaboration/members/?user_id=${userId}`,
  );
  return res.data;
};

// ===============================
// TeamCollabApis.js (ADD ONLY)
// ===============================

export const saveBgvRecord = async ({
  modalMode,
  employeeId,
  payload,
  token,
}) => {
  const path =
    modalMode === "create"
      ? `/api/team-collaboration/bgv`
      : `/api/team-collaboration/bgv/${employeeId}/update/`;

  const method = modalMode === "create" ? "post" : "put";

  const res = await apiConfig[method](path, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createPerformanceRecord = async ({ payload, token }) => {
  const res = await apiConfig.post(
    `/api/team-collaboration/performance`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

export const refreshMembersByUser = async (userId) => {
  const res = await apiConfig.get(
    `/api/team-collaboration/members/?user_id=${userId}`,
  );
  return res.data;
};

// ===============================
// TeamCollabApis.js (ADD ONLY)
// ===============================

// Upload BGV / general file
export const uploadEmployeeFile = async ({ employeeId, formData, userId }) => {
  const res = await apiConfig.post(
    `/api/team-collaboration/${employeeId}/upload-file/`,
    formData,
  );
  return res.data;
};

// Upload performance file
export const uploadPerformanceFile = async ({
  employeeId,
  formData,
  userId,
}) => {
  const res = await apiConfig.post(
    `/api/team-collaboration/${employeeId}/upload-performace-file/`,
    formData,
  );
  return res.data;
};

// ===============================
// TeamCollabApis.js (ADD ONLY)
// ===============================

export const createRelievedEmployeeRecord = async ({ payload, token }) => {
  const res = await apiConfig.post(
    `/api/team-collaboration/relieved-employees`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

// ===============================
// TeamCollabApis.js (ADD ONLY)
// ===============================

export const getBgvAgencies = async () => {
  const res = await apiConfig.get(`/api/bgv/agencies/`);
  return res.data; // { agencies: [...] }
};

// ===============================
// TeamCollabApis.js (ADD ONLY)
// ===============================

// 1️⃣ Create admin user
export const createAdminUser = async (payload) => {
  const firstname = String(payload?.firstname || "").trim();
  const email = String(payload?.email || "").trim();

  if (!firstname) {
    throw new Error("First name is required");
  }

  if (!email) {
    throw new Error("Email is required");
  }

  const formData = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  const res = await apiConfig.post(`/api/admin-user/create-user/`, formData);
  const data = res.data;

  if (data.status !== "success") {
    throw new Error(data?.error || data?.message || "Error creating member");
  }

  return data; // includes data.data.id
};

// 2️⃣ Update BGV for employee
export const updateEmployeeBgv = async ({ employeeId, agencyId, userId }) => {
  const res = await apiConfig.patch(
    `/api/bgv/update/${employeeId}/`,
    { verification_status: 1, agency_id: agencyId },
  );
  return res.data;
};

export const syncOdooUsers = async (customerMasterId, userId) => {
  try {
    if (!customerMasterId) {
      throw new Error("customer_master_id is required");
    }

    const payload = {
      customer_master_id: customerMasterId,
      created_by_user_id: userId,
    };

    const response = await apiConfig.post(
      `/api/team-collaboration/create-end-users-from-hrms/`,
      payload,
    );

    if (response.status !== 200) {
      throw new Error(response.data?.message || "Failed to sync Odoo data");
    }

    return response.data;
  } catch (error) {
    console.error("❌ syncOdooData API error:", error);
    throw error;
  }
};

// Fetch employees from Odoo without creating users in DB (NEW Odoo integration)
export const fetchOdooEmployeesAPI = async (customerMasterId) => {
  try {
    if (!customerMasterId) {
      throw new Error("customer_master_id is required");
    }

    const payload = {
      customer_master_id: customerMasterId,
    };

    const response = await apiConfig.post(
      `/api/team-collaboration/create-end-users-from-hrms/`,
      payload,
    );

    if (response.status !== 200) {
      throw new Error(
        response.data?.message || "Failed to fetch Odoo employees",
      );
    }

    return response.data;
  } catch (error) {
    console.error("❌ fetchOdooEmployees API error:", error);
    throw error;
  }
};

// Generate api function to call path("get-company-employees-for-chart/", get_company_employees_for_chart, name="get_company_employees_for_chart") use this path and request will include customer_master_id as query param, use api config as used for syncOdoousers

export const getCompanyEmployeesForChartApi = async (customerMasterId) => {
  try {
    if (!customerMasterId) {
      throw new Error("customer_master_id is required");
    }
    const response = await apiConfig.get(
      `/api/team-collaboration/get-company-employees-for-chart/?customer_master_id=${customerMasterId}`,
    );

    if (response.status !== 200) {
      throw new Error(
        response.data?.message || "Failed to fetch employee chart data",
      );
    }

    return response.data;
  } catch (error) {
    console.error("❌ getCompanyEmployeesForChartApi error:", error);
    throw error;
  }
};

export const getDepartments = async () => {
  try {
    const response = await apiConfig.get(
      `/api/team-collaboration/departments/`,
    );

    if (response.status !== 200) {
      throw new Error(response.data?.message || "Failed to fetch departments");
    }

    return response.data;
  } catch (error) {
    console.error("❌ getDepartments error:", error);
    throw error;
  }
};

export const getRoles = async () => {
  try {
    const response = await apiConfig.get(`/api/team-collaboration/roles/`);

    if (response.status !== 200) {
      throw new Error(response.data?.message || "Failed to fetch roles");
    }

    return response.data;
  } catch (error) {
    console.error("❌ getRoles error:", error);
    throw error;
  }
};

export const getRolesByDepartment = async (departmentId) => {
  try {
    if (!departmentId) {
      throw new Error("department_id is required");
    }

    const response = await apiConfig.get(
      `/api/team-collaboration/roles-by-department/?department_id=${departmentId}`,
    );

    if (response.status !== 200) {
      throw new Error(response.data?.message || "Failed to fetch roles");
    }
    return response.data;
  } catch (error) {
    console.error("❌ getRolesByDepartment error:", error);
    throw error;
  }
};

export const exportTeamMembersExcel = async (customerMasterId) => {
  try {
    if (!customerMasterId) {
      throw new Error("customer_master_id is required");
    }
    const response = await apiConfig.get(
      `/api/team-collaboration/export-team-members/?customer_master_id=${customerMasterId}`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team_members.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("❌ exportTeamMembersExcel error:", error);
    throw error;
  }
};

export const downloadSampleTeamTemplate = async () => {
  try {
    const response = await apiConfig.get(
      `/api/team-collaboration/sample-team-template/`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team_members_template.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("❌ downloadSampleTeamTemplate error:", error);
    throw error;
  }
};

export const bulkUploadTeamMembers = async (customerMasterId, file) => {
  try {
    if (!customerMasterId || !file) {
      throw new Error("customer_master_id and file are required");
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("customer_master_id", customerMasterId);

    const response = await apiConfig.post(
      `/api/team-collaboration/bulk-upload-members/`,
      formData,
    );
    return response.data;
  } catch (error) {
    console.error("❌ bulkUploadTeamMembers error:", error);
    throw error;
  }
};
